import { FC, useCallback, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import { TPgAlert, TPgAlertAction } from 'services/api/paymentGateway/types';
import { notificationContainer } from 'utils/notificationContainer';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgFields, PgJsonView } from '../common/PgFields';
import { PgStatusChip } from '../common/PgMiniTable';
import { usePaymentViewer } from '../Payments/PaymentViewer';
import { formatDateTime } from '../utils';

type TAlertDialogProps = {
	/** `null` — closed. */
	alertId: number | null;
	onClose: () => void;
	/** The alert was changed: the list should re-read. */
	onChanged: () => void;
};

type TNoteAction = Exclude<TPgAlertAction, 'acknowledge'>;

const NOTE_ACTIONS: Record<
	TNoteAction,
	{ title: string; button: string; description: string; color: 'primary' | 'error' | 'warning' }
> = {
	resolve: {
		title: 'Resolve the alert',
		button: 'Resolve',
		description: 'Marks the alert as handled. Write what was done.',
		color: 'primary',
	},
	rollback_eas: {
		title: 'Rollback in EAS',
		button: 'Rollback in EAS',
		description:
			'The payment was reversed at the provider (Direct Debit failed late, or a card dispute was lost). ' +
			'This reduces the paid amount of the invoice in EAS by the disputed amount (the corrections of ' +
			'refunds already made are not taken twice), so the invoice becomes payable again. The EAS core ' +
			'may send the customer an "Invoice payment received" e-mail on this update. It is performed ' +
			'in the background and cannot be repeated.',
		color: 'error',
	},
	reapply_eas: {
		title: 'Re-apply payment in EAS',
		button: 'Re-apply payment',
		description:
			'The dispute was reversed in favour of the merchant, and the payment was rolled back in EAS ' +
			'earlier. This adds the reversed amount to the paid amount of the invoice in EAS again. The EAS ' +
			'core may send the customer an "Invoice payment received" e-mail on this update.',
		color: 'warning',
	},
};

/** Alert card (03 §4.12) with the actions the gateway allows for it right now (`availableActions`). */
export const AlertDialog: FC<TAlertDialogProps> = ({ alertId, onClose, onChanged }) => {
	const { openPayment } = usePaymentViewer();
	const [alert, setAlert] = useState<TPgAlert | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [acting, setActing] = useState(false);
	const [noteAction, setNoteAction] = useState<TNoteAction | null>(null);
	const [noteError, setNoteError] = useState<string | null>(null);

	const load = useCallback(async (id: number) => {
		setLoading(true);
		setError(null);
		try {
			setAlert(await api.paymentGateway.getAlert(id));
		} catch (e) {
			setError(getPgErrorMessage(e, 'Failed to load the alert'));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		setAlert(null);
		setError(null);
		setNoteAction(null);
		if (alertId) void load(alertId);
	}, [alertId, load]);

	const acknowledge = async () => {
		if (!alert) return;
		setActing(true);
		try {
			setAlert(await api.paymentGateway.acknowledgeAlert(alert.id));
			notificationContainer('Alert acknowledged', 'success');
			onChanged();
		} catch (e) {
			notificationContainer(getPgErrorMessage(e, 'Failed to acknowledge the alert'), 'error');
		} finally {
			setActing(false);
		}
	};

	const submitNote = async (note: string) => {
		if (!alert || !noteAction) return;
		setActing(true);
		setNoteError(null);
		try {
			if (noteAction === 'resolve') {
				setAlert(await api.paymentGateway.resolveAlert({ id: alert.id, body: { note } }));
				notificationContainer('Alert resolved', 'success');
			} else {
				const call =
					noteAction === 'rollback_eas'
						? api.paymentGateway.rollbackEasForAlert
						: api.paymentGateway.reapplyEasForAlert;
				await call({ id: alert.id, body: { note } });
				notificationContainer(
					'Accepted: Invoicing performs the change in EAS in the background',
					'success',
				);
				await load(alert.id);
			}
			setNoteAction(null);
			onChanged();
		} catch (e) {
			setNoteError(getPgErrorMessage(e, 'The action failed'));
		} finally {
			setActing(false);
		}
	};

	const available = alert?.availableActions ?? [];
	const noteConfig = noteAction ? NOTE_ACTIONS[noteAction] : null;

	return (
		<>
			<Dialog open={!!alertId} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
				<DialogTitle
					color="primary"
					sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
				>
					Alert
					{alert && <PgStatusChip value={alert.severity} />}
					{alert && <PgStatusChip value={alert.status} />}
				</DialogTitle>
				<DialogContent dividers>
					{loading && !alert && <CircularProgress size={24} />}
					{error && <Alert severity="error">{error}</Alert>}
					{alert && (
						<>
							<Typography variant="subtitle1" fontWeight="bold">
								{alert.title}
							</Typography>
							{alert.message && (
								<Typography variant="body2" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
									{alert.message}
								</Typography>
							)}
							<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
								{available.includes('acknowledge') && (
									<Button
										size="small"
										variant="outlined"
										onClick={() => void acknowledge()}
										disabled={acting}
									>
										Acknowledge
									</Button>
								)}
								{available.includes('resolve') && (
									<Button
										size="small"
										variant="contained"
										onClick={() => {
											setNoteError(null);
											setNoteAction('resolve');
										}}
										disabled={acting}
									>
										Resolve
									</Button>
								)}
								{available.includes('rollback_eas') && (
									<Button
										size="small"
										variant="contained"
										color="error"
										onClick={() => {
											setNoteError(null);
											setNoteAction('rollback_eas');
										}}
										disabled={acting}
									>
										Rollback in EAS
									</Button>
								)}
								{available.includes('reapply_eas') && (
									<Button
										size="small"
										variant="contained"
										color="warning"
										onClick={() => {
											setNoteError(null);
											setNoteAction('reapply_eas');
										}}
										disabled={acting}
									>
										Re-apply payment in EAS
									</Button>
								)}
								{alert.paymentTransactionId && (
									<Button
										size="small"
										onClick={() =>
											alert.paymentTransactionId && openPayment(alert.paymentTransactionId)
										}
									>
										Open the payment
									</Button>
								)}
							</Box>
							<PgFields
								items={[
									['Type', alert.type],
									['Occurrences', alert.occurrences],
									['First seen', formatDateTime(alert.firstSeenAt)],
									['Last seen', formatDateTime(alert.lastSeenAt)],
									['Customer ID', alert.customerId],
									['Invoice ID', alert.invoiceId],
									[
										'Acknowledged',
										alert.acknowledgedAt
											? `${formatDateTime(alert.acknowledgedAt)} by ${alert.acknowledgedBy ?? '—'}`
											: null,
									],
									[
										'Resolved',
										alert.resolvedAt
											? `${formatDateTime(alert.resolvedAt)} by ${alert.resolvedBy ?? '—'}`
											: null,
									],
									['Resolution note', alert.resolutionNote],
								]}
							/>
							<Typography variant="subtitle2" color="primary" sx={{ mt: 2, mb: 0.5 }}>
								Details
							</Typography>
							<PgJsonView value={alert.details} />
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={onClose}>Close</Button>
				</DialogActions>
			</Dialog>
			<PgConfirmDialog
				open={!!noteConfig}
				title={noteConfig?.title ?? ''}
				description={noteConfig?.description}
				confirmLabel={noteConfig?.button ?? 'Confirm'}
				confirmColor={noteConfig?.color}
				reasonLabel="Note (shown in the audit log)"
				busy={acting}
				error={noteError}
				onConfirm={(note) => void submitNote(note)}
				onClose={() => setNoteAction(null)}
			/>
		</>
	);
};
